import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { Address, AvailableOptions, SetOptions, SetOptionsPayload } from '@checkout/const';
import {
  AdditionalOptionsFormService,
  AdditionalOptionsService,
  CheckoutContactFormService,
  CustomCheckoutService,
  DeliveryAddressService,
  ReceiptService,
} from '@checkout/feature';
import { CityService } from '@city/feature';
import { concatLatestFrom } from '@ngrx/operators';
import { CUSTOM_ICON_TYPE } from '@shared/models';
import { DeviceService } from '@shared/services';
import { ModalService } from '@spartacus/storefront';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { SdvorDeliveryAddressDialogModalComponent } from '../receipt/receipt-address/delivery/delivery-address-dialog/delivery-address-dialog-modal/delivery-address-dialog-modal.component';

@Component({
  selector: 'sd-additional-options',
  templateUrl: './additional-options.component.html',
  styleUrls: ['./additional-options.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SdvorAdditionalOptionsComponent implements OnInit, OnDestroy {
  iconType = CUSTOM_ICON_TYPE;
  form: FormGroup;
  availableOptions$: Observable<AvailableOptions>;
  loading$: Observable<boolean>;
  activeAddressId$: Observable<string | undefined>;
  address$: Observable<Address>;
  priceProofText$: Observable<string>;
  receiptType$: Observable<string>;
  baseStoreCode$: Observable<string>;
  isDesktop$ = this.deviceService.isDesktopLgUp$;
  popupIsOpened$ = new BehaviorSubject<boolean>(false);

  constructor(
    private additionalOptionsService: AdditionalOptionsService,
    private additionalOptionsFormService: AdditionalOptionsFormService,
    private receiptService: ReceiptService,
    private deliveryAddressService: DeliveryAddressService,
    private modalService: ModalService,
    private checkoutContactFormService: CheckoutContactFormService,
    private deviceService: DeviceService,
    private customCheckoutService: CustomCheckoutService,
    private cityService: CityService,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.loading$ = this.additionalOptionsService.getLoading();
    this.form = this.additionalOptionsFormService.createForm();
    this.availableOptions$ = this.additionalOptionsService.getAvailableOptions();

    this.activeAddressId$ = this.deliveryAddressService.activeAddressId$;
    this.address$ = this.activeAddressId$.pipe(
      filter((id): id is string => Boolean(id)),
      switchMap((id) => this.deliveryAddressService.getAddress(id)),
      filter<Address>(Boolean),
    );

    this.receiptType$ = this.receiptService.activeReceiptTypeId$;
    this.baseStoreCode$ = this.cityService.activeBaseStoreCode$;

    combineLatest({
      consignment: this.customCheckoutService.getCustomerConsignment(),
      address: this.address$,
      baseStoreCode: this.baseStoreCode$,
    })
      .pipe(
        map(({ consignment, address, baseStoreCode }) => ({
          deliveryModeId: consignment?.deliveryModeId,
          deliveryAddressId: consignment?.deliveryAddressId,
          address,
          baseStoreCode,
        })),
        distinctUntilChanged((prev, next) => {
          return (
            prev.deliveryModeId === next.deliveryModeId &&
            prev.deliveryAddressId === next.deliveryAddressId &&
            prev.baseStoreCode === next.baseStoreCode &&
            // поля адреса, влияющие на доступность/стоимость доп. услуг
            prev.address.cargoLift === next.address.cargoLift &&
            prev.address.floor === next.address.floor &&
            prev.address.point?.latitude === next.address.point?.latitude &&
            prev.address.point?.longitude === next.address.point?.longitude &&
            prev.address.hasALift === next.address.hasALift &&
            prev.address.heightLimit === next.address.heightLimit &&
            prev.address.passengerLift === next.address.passengerLift &&
            prev.address.privateHouse === next.address.privateHouse
          );
        }),
        tap(() => {
          // очистка значений формы должна быть в том числе и при переключении на самовывоз
          this.additionalOptionsFormService.reset();
        }),
        filter(({ deliveryModeId }) => deliveryModeId === 'delivery'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.additionalOptionsService.refreshOptions();
      });

    const optionControl = this.form.get('option');
    if (optionControl) {
      optionControl.valueChanges
        .pipe(
          concatLatestFrom(() => [
            this.availableOptions$,
            this.activeAddressId$,
            this.checkoutContactFormService.isSubmitted$,
          ]),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(([, options, addressId, confirmed]) => {
          if (addressId && options && !confirmed) {
            this.additionalOptionsService.setActiveOptions(this.getOptionPayloadData(options));
          }
        });
    }

    this.priceProofText$ = this.address$.pipe(
      map((address: Address) => {
        return `Стоимость рассчитана для ${address.floor} этажа ${
          address.cargoLift || address.passengerLift ? 'c' : 'без лифта'
        }
        ${address.cargoLift && address.passengerLift ? 'грузовым и пассажирским лифтом' : ''}
        ${address.cargoLift && !address.passengerLift ? 'грузовым лифтом' : ''}
        ${!address.cargoLift && address.passengerLift ? 'пассажирским лифтом' : ''}
        `;
      }),
    );
  }

  ngOnDestroy(): void {
    this.additionalOptionsFormService.reset();
  }

  getOptionPayloadData(options: AvailableOptions): SetOptionsPayload {
    const optionControl = this.form.get('option');
    const activeOption = optionControl ? (optionControl.value as SetOptions['code'] | 'noOption') : undefined;

    if (!activeOption || activeOption === 'noOption' || !options[activeOption]) {
      return { services: [] };
    }

    const service: SetOptions = {
      code: activeOption,
      cost: options[activeOption].price,
    };

    return { services: [service] };
  }

  openPopup(): void {
    this.popupIsOpened$.next(true);
  }

  closePopup(): void {
    this.popupIsOpened$.next(false);
  }

  editAddress(): void {
    this.activeAddressId$.pipe(take(1)).subscribe((id) => {
      if (id) {
        this.deliveryAddressService.editableAddressId$.next(id);
        this.modalService.open(SdvorDeliveryAddressDialogModalComponent, {
          centered: true,
          size: 'xl',
        });
      }
    });
  }
}
